/**
 * Membership List Component (User detail page → Memberships section)
 *
 * A membership links a user → organization → role (org-scoped, vertical model).
 * On a user's detail page an admin can create a membership (pick an organization + a
 * role), see the user's existing memberships (organization, role, status), and remove one.
 *
 * Endpoints (real backend, feat/org-vertical-model-for-accounts):
 * - GET    /mint/memberships?userNumber=                                  (getMemberships)         — per-user list
 * - POST   /mint/organizations/:orgNumber/memberships                     (upsertOrganizationMembership)  — create
 * - DELETE /mint/organizations/:orgNumber/memberships/:membershipNumber   (archiveOrganizationMembership) — archive
 *
 * The role is a hardcoded mock (roleNumber 1000005001, the seeded Admin role) sent behind the scenes; the backend
 * roles table isn't seeded yet; swap back to GET /reMa/roles (getRoles) once real roles
 * exist. The backend rejects an unknown roleNumber with ROLE_NOT_FOUND. Creating a
 * membership requires the org's Farmgrid account to be provisioned and not inactive.
 */

import * as React from 'react'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ProfileSection } from '@/components/sensnet/ProfileLayout'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { Trash2, Plus, Users } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Types
// ============================================================================

interface Membership {
  id: string
  membershipNumber: number
  orgNumber: number
  organizationName: string
  // Role is nullable and always null until roles are reimplemented (backend v1.0.36).
  roleName: string
  status: string // 'active' | 'inactive'
  archived: boolean
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// Map an API membership record (nested organization; role is nullable) to a table row.
const toRow = (m: any): Membership => ({
  id: String(m.membershipNumber),
  membershipNumber: m.membershipNumber,
  orgNumber: m.organization?.orgNumber,
  organizationName: m.organization?.name ?? `Org ${m.organization?.orgNumber}`,
  // role is always null for now — show a dash rather than crashing on null.
  roleName: m.role?.roleName ?? '—',
  status: m.archiveDate ? 'Archived' : cap(m.status ?? 'active'),
  archived: !!m.archiveDate,
})

// ============================================================================
// Props
// ============================================================================

export interface MembershipListProps {
  userNumber: number
  /** When the user account is archived, hide add/remove actions. */
  disabled?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function MembershipList(props: MembershipListProps) {
  const { userNumber, disabled = false } = props
  const { toast } = useToast()

  // Per-user membership list.
  const { data } = useApiQuery('getMemberships', { userNumber }, { retry: 0 })
  const list: Membership[] = ((data as any)?.content?.memberships ?? []).map(toRow)

  // Organization options for the "Add membership" dropdown.
  const { data: orgsData } = useApiQuery('getOrganizations', {}, {})
  const orgOptions: { value: string; label: string }[] = (
    (orgsData as any)?.content?.organizations ?? []
  )
    .filter((o: any) => !o.archiveDate)
    .map((o: any) => ({ value: String(o.orgNumber), label: o.name ?? `Org ${o.orgNumber}` }))

  // Role is no longer sent on membership create (backend v1.0.36 dropped roleNumber from the
  // request; role is nullable and always null until roles are reimplemented).

  // Mutations — memberships are the user↔org link, so also invalidate getUserByNumber (the
  // detail page's Organizations card) and getUsers (the list's Organizations column) so they
  // don't go stale after add/remove on this page.
  const upsertMembership = useApiMutation('upsertOrganizationMembership', 'POST', {
    invalidates: ['getMemberships', 'getUserByNumber', 'getUsers'],
  })
  const archiveMembership = useApiMutation('archiveOrganizationMembership', 'DELETE', {
    invalidates: ['getMemberships', 'getUserByNumber', 'getUsers'],
  })

  const [isAddOpen, setIsAddOpen] = React.useState(false)
  const [selectedOrg, setSelectedOrg] = React.useState<string>('')
  const [removing, setRemoving] = React.useState<Membership | null>(null)

  const canManage = !disabled
  const busy = upsertMembership.isPending || archiveMembership.isPending

  const resetAdd = () => {
    setIsAddOpen(false)
    setSelectedOrg('')
  }

  // --- Add ------------------------------------------------------------------
  const handleAddSubmit = async () => {
    if (!selectedOrg) {
      toast({ title: 'Select an organization', description: 'Please choose an organization.', type: 'error' })
      return
    }
    // Select controls emit strings — coerce the id to a number for the request.
    const orgNumber = Number(selectedOrg)
    try {
      // Path { orgNumber }; strict body { userNumber } (orgNumber stripped in the URL).
      // roleNumber is no longer sent (backend v1.0.36).
      await upsertMembership.mutateAsync({ orgNumber, userNumber })
      const orgLabel = orgOptions.find((o) => o.value === selectedOrg)?.label ?? `Org ${orgNumber}`
      resetAdd()
      toast({ title: 'Membership added', description: `Added to ${orgLabel}.`, type: 'success' })
    } catch (err) {
      log.error('Failed to create membership', err)
      toast({ title: 'Could not add membership', description: (err as Error)?.message || 'Please try again.', type: 'error' })
    }
  }

  // --- Remove ---------------------------------------------------------------
  const handleRemoveConfirm = async () => {
    if (!removing) return
    try {
      await archiveMembership.mutateAsync({
        orgNumber: removing.orgNumber,
        membershipNumber: removing.membershipNumber,
      })
      setRemoving(null)
      toast({ title: 'Membership removed', description: 'The membership has been removed.', type: 'success' })
    } catch (err) {
      log.error('Failed to remove membership', err)
      toast({ title: 'Could not remove membership', description: (err as Error)?.message || 'Please try again.', type: 'error' })
    }
  }

  return (
    <>
      <ProfileSection
        title={`Memberships (${list.length})`}
        icon={<Users className="h-4 w-4" />}
        variant="dark"
        action={canManage && (
          <Button onClick={() => setIsAddOpen(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Membership
          </Button>
        )}
      >
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No memberships found.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5 text-left">Organization</TableHead>
                <TableHead className="text-left">Role</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="pr-5 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((m) => (
                <TableRow key={m.id} className="hover:bg-muted/50">
                  <TableCell className="pl-5 font-medium">{m.organizationName}</TableCell>
                  <TableCell>{m.roleName}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant={m.archived || m.status === 'Inactive' ? 'secondary' : 'default'}>
                      {m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="pr-5 text-right">
                    {canManage && !m.archived && (
                      <Button variant="ghost" size="sm" onClick={() => setRemoving(m)}>
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remove membership</span>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ProfileSection>

      {/* Add Membership dialog — organization only (role is not set here; v1.0.36) */}
      <Dialog open={isAddOpen} onOpenChange={(open) => (open ? setIsAddOpen(true) : resetAdd())}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Membership</DialogTitle>
            <DialogDescription>Give this user access to an organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Organization</Label>
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger>
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">No organizations available</div>
                  ) : (
                    orgOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetAdd}>
              Cancel
            </Button>
            <Button onClick={handleAddSubmit} disabled={busy}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirm */}
      <Dialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove membership?</DialogTitle>
            <DialogDescription>
              This will remove the user's membership to {removing?.organizationName} ({removing?.roleName}).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveConfirm} disabled={busy}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
